 import { useState, useCallback } from 'react';
 import { isPlaceholderImage, getCategoryDefaultImage, getCategoryLogoUrl } from '@/utils/imageUtils';
 import { useOptimizedImage, IMAGE_SIZES } from '@/hooks/useOptimizedImage';
 import { cn } from '@/lib/utils';
 
 interface BrandedImageProps {
   src: string | null | undefined;
   category: string;
   alt: string;
   className?: string;
   containerClassName?: string;
   showLogoOnPlaceholder?: boolean;
   logoPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
   logoSize?: 'sm' | 'md' | 'lg';
   priority?: boolean;
   sizes?: string;
   aspectRatio?: string;
   width?: number;
 }
 
 const LOGO_POSITION_CLASSES: Record<string, string> = {
   'top-left': 'top-3 left-3',
   'top-right': 'top-3 right-3',
   'bottom-left': 'bottom-3 left-3',
   'bottom-right': 'bottom-3 right-3',
 };
 
 const LOGO_SIZE_CLASSES: Record<string, string> = {
   sm: 'w-10 h-10',
   md: 'w-14 h-14',
   lg: 'w-20 h-20',
 };
 
 export const BrandedImage = ({
   src,
   category,
   alt,
   className,
   containerClassName,
   showLogoOnPlaceholder = true,
   logoPosition = 'bottom-right',
   logoSize = 'md',
   priority = false,
   sizes = IMAGE_SIZES.card,
   aspectRatio = 'aspect-video',
   width = 400,
 }: BrandedImageProps) => {
   const [hasError, setHasError] = useState(false);
   const [isLoaded, setIsLoaded] = useState(false);
 
   // Determine if original source is a placeholder
   const isOriginalPlaceholder = isPlaceholderImage(src);
   
   // Should we use the fallback? Either original is placeholder or image failed to load
   const useFallback = isOriginalPlaceholder || hasError;
   
   // Get the effective image source
   const effectiveSrc = useFallback ? getCategoryDefaultImage(category) : src!;
   
   // Get logo URL for overlay
   const logoUrl = getCategoryLogoUrl(category);
   
   // Should we show the logo overlay?
   const shouldShowLogo = showLogoOnPlaceholder && useFallback && logoUrl;
   
   // Use optimized image hook
   const { src: optimizedSrc, srcSet, blurDataUrl } = useOptimizedImage(effectiveSrc, { width });
 
   const handleError = useCallback(() => {
     if (!hasError && !isOriginalPlaceholder) {
       setHasError(true);
     }
   }, [hasError, isOriginalPlaceholder]);
 
   const handleLoad = useCallback(() => {
     setIsLoaded(true);
   }, []);
 
   return (
     <div className={cn('relative overflow-hidden', aspectRatio, containerClassName)}>
       {/* Blur placeholder */}
       {blurDataUrl && !isLoaded && (
         <img
           src={blurDataUrl}
           alt=""
           aria-hidden="true"
           className="absolute inset-0 w-full h-full object-cover blur-lg scale-110"
         />
       )}
       
       {/* Main image */}
       <img
         src={optimizedSrc}
         srcSet={srcSet}
         sizes={sizes}
         alt={alt}
         loading={priority ? 'eager' : 'lazy'}
          {...(priority ? { fetchpriority: 'high' } : {})}
         onError={handleError}
         onLoad={handleLoad}
         className={cn(
           'w-full h-full object-cover transition-opacity duration-300',
           isLoaded ? 'opacity-100' : 'opacity-0',
           className
         )}
       />
       
       {/* Logo overlay - only shown on placeholder/fallback images */}
       {shouldShowLogo && (
         <div
           className={cn(
             'absolute z-10 transition-opacity duration-300',
             LOGO_POSITION_CLASSES[logoPosition],
             isLoaded ? 'opacity-100' : 'opacity-0'
           )}
         >
           <div className={cn(
             'bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-1.5',
             LOGO_SIZE_CLASSES[logoSize]
           )}>
             <img
               src={logoUrl}
               alt={`${category} logo`}
               className="w-full h-full object-contain"
             />
           </div>
         </div>
       )}
     </div>
   );
 };